import axios from 'axios';
import * as Sentry from '@sentry/react';
import { GOV_API_ENDPOINTS, currentAppUrls, isDevelopment } from '../config/urls';
import { setRedirectAfterLogin } from '../slices/authSlice';
import { retryWithBackoff } from '../utils/retryUtils';
import { globalApiScheduler } from '../api/globalApiScheduler';
import { invoiceRequestManager, isInvoiceRequestCanceled } from '../api/invoiceRequestManager';
import websocketService from './websocketService';
import {
  getIsRefreshing,
  getRefreshPromise,
  setRefreshPromise,
  queueRequest,
  processAndClearQueue,
  setRefreshing
} from './tokenManager';

// Cookie parsing helper for readable cookies set by server
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const getCSRFToken = () => {
  return getCookie('csrfToken') || sessionStorage.getItem('csrfToken');
};

const normalizeErrorResponseData = async (data) => {
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return data;

  try {
    return JSON.parse(await data.text());
  } catch {
    return data;
  }
};

// Shared error-path for the many service methods that log a message and rethrow.
const logRethrow = (msg, error) => {
  console.error(msg, error.response?.data || error.message);
  throw error;
};

// Create axios instance for API requests
const apiClient = axios.create({
  baseURL: currentAppUrls.API_BASE || '',
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true, // IMPORTANT: Send cookies with every request
  timeout: 30000 // 30 seconds timeout for complex operations (was 10s)
});

// Create a separate instance for quick operations (health checks, etc.)
const quickApiClient = axios.create({
  baseURL: currentAppUrls.API_BASE || '',
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 10000 // 10 seconds for quick operations
});

const installGlobalScheduler = (client) => {
  const originalRequest = client.request.bind(client);
  client.request = (config) => globalApiScheduler.request(originalRequest, config);
};

installGlobalScheduler(apiClient);
installGlobalScheduler(quickApiClient);

// API base URL configured

// Rate limiting configuration
const RATE_LIMIT_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
};

// Exponential backoff delay calculation
const getRetryDelay = (attempt) => {
  const delay = Math.min(
    RATE_LIMIT_CONFIG.baseDelay * Math.pow(2, attempt),
    RATE_LIMIT_CONFIG.maxDelay
  );
  // Add jitter to prevent thundering herd
  return delay + Math.random() * 1000;
};

// Sentry throttling: track last capture time per endpoint
const lastSentryCapture = new Map();
const SENTRY_THROTTLE_MS = 60000; // 1 capture per endpoint per 60 seconds

// Circuit breaker: fail fast after consecutive failures
const circuitBreakerState = new Map();
const CIRCUIT_BREAKER_THRESHOLD = 3; // trips after 3 consecutive failures
const CIRCUIT_BREAKER_RESET_MS = 30000; // resets after 30 seconds

const API_TIMING_THRESHOLDS_MS = [100, 300, 1000, 3000];
const apiTimingSamples = [];
const API_TIMING_SAMPLE_LIMIT = 200;
const shouldLogApiTiming = () => {
  if (isDevelopment) return true;
  if (typeof window === 'undefined') return false;
  return window.localStorage?.getItem('GIS_API_TIMING_LOGS') === 'true';
};

const getRequestLabel = (config = {}) => {
  const method = String(config.method || 'GET').toUpperCase();
  const url = config.url || '';
  return `${method} ${url}`;
};

const recordApiTiming = (config = {}, status, error = null) => {
  const startedAt = config.metadata?.startedAt;
  if (!startedAt) return;

  const durationMs = Math.round(performance.now() - startedAt);
  const threshold = API_TIMING_THRESHOLDS_MS.find(ms => durationMs >= ms);
  const timing = {
    method: String(config.method || 'GET').toUpperCase(),
    url: config.url || '',
    status,
    durationMs,
    startedAt: config.metadata.startedAtWallTime,
    endedAt: new Date().toISOString(),
    error: error?.message || null
  };

  apiTimingSamples.push(timing);
  if (apiTimingSamples.length > API_TIMING_SAMPLE_LIMIT) {
    apiTimingSamples.shift();
  }

  if (threshold && shouldLogApiTiming()) {
    const log = durationMs >= 1000 ? console.warn : console.info;
    log(`[API_TIMING] ${getRequestLabel(config)} ${status || 'ERR'} took ${durationMs}ms`, timing);
  }
};

if (typeof window !== 'undefined') {
  window.__GIS_API_TIMINGS__ = apiTimingSamples;
}

const shouldCaptureSentry = (endpoint) => {
  const now = Date.now();
  const lastCapture = lastSentryCapture.get(endpoint) || 0;
  if (now - lastCapture < SENTRY_THROTTLE_MS) {
    return false;
  }
  lastSentryCapture.set(endpoint, now);
  return true;
};

const recordFailure = (endpoint) => {
  const state = circuitBreakerState.get(endpoint) || { count: 0, lastFailure: 0 };
  state.count += 1;
  state.lastFailure = Date.now();
  circuitBreakerState.set(endpoint, state);
  return state.count >= CIRCUIT_BREAKER_THRESHOLD;
};

const isCircuitOpen = (endpoint) => {
  const state = circuitBreakerState.get(endpoint);
  if (!state || state.count < CIRCUIT_BREAKER_THRESHOLD) return false;
  if (Date.now() - state.lastFailure > CIRCUIT_BREAKER_RESET_MS) {
    circuitBreakerState.set(endpoint, { count: 0, lastFailure: 0 });
    return false;
  }
  return true;
};

// Debounce mechanism to prevent concurrent refresh attempts
const MAX_REFRESH_ATTEMPTS = 3;
let refreshAttempts = 0;
let lastRefreshTime = 0;
const REFRESH_COOLDOWN_MS = 5000; // 5 seconds between refresh attempts

// Function to refresh the access token
const refreshAccessToken = async () => {
  // DEBOUNCE: Use shared tokenManager state instead of local
  if (getIsRefreshing()) {
    return getRefreshPromise();
  }

  const now = Date.now();

  // Check if we're in cooldown period
  if (now - lastRefreshTime < REFRESH_COOLDOWN_MS) {
    await new Promise(resolve => setTimeout(resolve, REFRESH_COOLDOWN_MS - (now - lastRefreshTime)));
  }

  // Check if we've exceeded max attempts
  if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
    console.error('[apiService] Max refresh attempts exceeded, forcing logout.');
    refreshAttempts = 0;
    throw new Error('MAX_REFRESH_ATTEMPTS_EXCEEDED');
  }

  setRefreshing(true);

  const promise = (async () => {
    try {
      refreshAttempts++;
      lastRefreshTime = Date.now();

      // First try cookie-based refresh (more secure, httpOnly)
      const response = await apiClient.post('/auth/refresh-token', {}, { withCredentials: true });

      const { token: newAccessToken, csrfToken } = response.data;

      // Update in-memory Authorization header; tokens live in httpOnly cookies
      if (newAccessToken) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
      }
      if (csrfToken) {
        sessionStorage.setItem('csrfToken', csrfToken);
      }

      // Reset refresh attempts on success
      refreshAttempts = 0;
      return newAccessToken;
    } catch (error) {
      console.error('[apiService] Failed to refresh access token:', error.response?.data || error.message);

      const errorCode = error.response?.data?.code;

      if (errorCode === 'TOKEN_REUSE_DETECTED' ||
          errorCode === 'REFRESH_TOKEN_MISMATCH' ||
          errorCode === 'REFRESH_TOKEN_EXPIRED' ||
          errorCode === 'CONCURRENT_SESSION' ||
          error.response?.status === 403) {
        refreshAttempts = 0;

        const currentPath = window.location.pathname + window.location.search;
        setRedirectAfterLogin(currentPath);

        // TC-AUTH-008: REFRESH_TOKEN_MISMATCH and TOKEN_REUSE_DETECTED also indicate concurrent session
        if (errorCode === 'CONCURRENT_SESSION' ||
            errorCode === 'REFRESH_TOKEN_MISMATCH' ||
            errorCode === 'TOKEN_REUSE_DETECTED') {
          localStorage.setItem('concurrentSessionDetected', 'true');
          localStorage.setItem('concurrentSessionMessage', error.response?.data?.message || 'Logged in from another device. Please log in again.');
          window.dispatchEvent(new CustomEvent('auth:concurrentSession', {
            detail: {
              message: error.response?.data?.message || 'Logged in from another device. Please log in again.',
              code: errorCode || 'CONCURRENT_SESSION'
            }
          }));
        } else {
          window.dispatchEvent(new CustomEvent('auth:sessionExpired', {
            detail: {
              message: error.response?.data?.message || 'Your session has expired. Please log in again.',
              code: errorCode || 'SESSION_EXPIRED'
            }
          }));
        }

        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('loginTimestamp');
        delete apiClient.defaults.headers.common['Authorization'];

        throw new Error(error.response?.data?.message || 'Authentication failed - please log in again');
      }

      // If refresh fails due to other reasons, clear tokens and trigger logout
      const currentPath = window.location.pathname + window.location.search;
      setRedirectAfterLogin(currentPath);

      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      delete apiClient.defaults.headers.common['Authorization'];
      throw error;
    } finally {
      setRefreshing(false);
      setRefreshPromise(null);
    }
  })();

  setRefreshPromise(promise);
  return promise;
};

// Reset refresh attempts (call this on successful login)
export const resetRefreshAttempts = () => {
  refreshAttempts = 0;
  lastRefreshTime = 0;
};

// Proactive refresh called by AuthTimeoutManager before token expiry.
// Intentionally separate from refreshAccessToken (the 401-retry path) —
// fire-and-forget: success updates the in-memory header, failure lets the
// normal 401 flow handle it without burning the retry budget.
export const proactiveRefresh = async () => {
  try {
    const response = await apiClient.post('/auth/refresh-token', {}, { withCredentials: true });
    const { token: newAccessToken, csrfToken } = response.data;
    if (newAccessToken) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
    }
    if (csrfToken) {
      sessionStorage.setItem('csrfToken', csrfToken);
    }
    refreshAttempts = 0;
    return true;
  } catch {
    return false;
  }
};


// Request interceptor - authentication via httpOnly cookies only
// Tokens are stored in httpOnly cookies set by the server, not localStorage
apiClient.interceptors.request.use(config => {
  config.metadata = {
    ...(config.metadata || {}),
    startedAt: performance.now(),
    startedAtWallTime: new Date().toISOString(),
    // Stamp the auth generation at send time. A 401 from a request issued under an older generation
    // (i.e. before a fresh login) must not re-open the session-timeout modal after re-login.
    authGeneration: _authGeneration
  };

  // Always set withCredentials to send cookies with requests
  config.withCredentials = true;

  // SECURITY: Bypass token is now stored in httpOnly cookie by backend
  // It is automatically sent with requests via withCredentials: true
  // No need to read from localStorage or send as header

  // SECURITY: Add CSRF token for state-changing requests
  const csrfMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
  if (!config._skipCSRF && csrfMethods.includes(config.method?.toUpperCase())) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  // Cookies are automatically sent with withCredentials: true
  // Server extracts token from cookies via authMiddleware
  return config;
}, error => {
  // Only log actual request errors, not routine operations
  if (error && error.message) {
    console.error('[apiClient Interceptor] Request error:', error.message);
  }
  return Promise.reject(error);
});

const AUTO_RETRY_METHODS = new Set(['get', 'head', 'options']);
const shouldAutoRetryRequest = (config = {}) => {
  if (config._disableAutoRetry === true) return false;
  if (config._allowUnsafeRetry === true) return true;

  const method = (config.method || 'get').toLowerCase();
  const url = String(config.url || '');
  if (method === 'get' && (url === '/invoices' || url.startsWith('/invoices?'))) {
    return false;
  }

  return AUTO_RETRY_METHODS.has(method);
};

// Intercept responses to handle token expiration and rate limiting
apiClient.interceptors.response.use(
  response => {
    recordApiTiming(response.config, response.status);
    return response;
  },
  async error => { // Make the interceptor async
    const originalRequest = error.config;
    const errorData = await normalizeErrorResponseData(error.response?.data);
    recordApiTiming(originalRequest, error.response?.status || error.code || 'ERR', error);

    // Handle Rate Limiting (429) with exponential backoff retry
    if (error.response && error.response.status === 429 && shouldAutoRetryRequest(originalRequest)) {
      const retryCount = originalRequest._retryCount || 0;

      // Record failure for circuit breaker
      const endpoint = `${originalRequest.method}:${originalRequest.url}`;
      const circuitTripped = recordFailure(endpoint);

      if (retryCount < RATE_LIMIT_CONFIG.maxRetries && !circuitTripped) {
        originalRequest._retryCount = retryCount + 1;
        const delay = getRetryDelay(retryCount);

        // Only log retry attempt at info level (Sentry capture is throttled)
        if (shouldCaptureSentry(endpoint)) {
          Sentry.captureMessage('Rate limit hit (429)', {
            level: 'info',
            extra: {
              url: originalRequest.url,
              method: originalRequest.method,
              retryCount: retryCount + 1,
              delay: delay,
              circuitTripped
            }
          });
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        return apiClient(originalRequest);
      } else {
        // Max retries reached or circuit open, reject without Sentry spam
        console.error('[apiService] Max retries reached or circuit open for rate limited request:', originalRequest.url);
        if (shouldCaptureSentry(endpoint)) {
          Sentry.captureException(new Error('Rate limit max retries exceeded'), {
            extra: {
              url: originalRequest.url,
              method: originalRequest.method,
              maxRetries: RATE_LIMIT_CONFIG.maxRetries,
              circuitTripped
            }
          });
        }
      }
    }

    // Handle timeout errors (ECONNABORTED) with retry and exponential backoff
    if ((error.code === 'ECONNABORTED' || error.message?.includes('timeout')) && shouldAutoRetryRequest(originalRequest)) {
      const retryCount = originalRequest._retryCount || 0;

      if (retryCount < 3) {
        originalRequest._retryCount = retryCount + 1;
        // Exponential backoff: 2s, 4s, 8s with jitter
        const baseDelay = 2000;
        const delay = baseDelay * Math.pow(2, retryCount) + Math.random() * 1000;

        if (process.env.NODE_ENV === 'development') {
          console.log(`[apiService] Request timeout, retry ${retryCount + 1}/3 in ${Math.round(delay)}ms`);
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        return apiClient(originalRequest);
      }
    }

    let errorMessage = 'An unexpected error occurred.';

    if (error.response?.data && (error.response.data.status === 'error' || error.response.data.status === 'failed') && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Assign extracted message so callers reading error.message get the server message, not axios generic
    error.message = errorMessage;

    // Capture API errors in Sentry (throttled, except 401/403/429 which are handled separately, 413 is payload too large)
    if (error.response && ![401, 403, 429, 413].includes(error.response.status)) {
      const endpoint = `${error.config?.method || 'GET'}:${error.config?.url || originalRequest.url}`;
      if (shouldCaptureSentry(endpoint)) {
        Sentry.captureException(error, {
          extra: {
            url: originalRequest.url,
            method: originalRequest.method,
            status: error.response.status,
            responseData: error.response.data
          }
        });
      }
    }

    // Recover once when a session has no CSRF token (e.g. reopened tab: auth cookie persists but the
    // sessionStorage token is gone). Server errors use the nested { error: { code } } shape; keep the
    // flat fallback for any endpoint that returns a top-level code.
    const csrfErrCode = errorData?.error?.code ?? errorData?.code;
    if (typeof csrfErrCode === 'string' && csrfErrCode.startsWith('CSRF_')) {
      error.message = errorData?.error?.message || errorData?.message || error.message;
      if (originalRequest && !originalRequest._csrfRetry) {
        originalRequest._csrfRetry = true;

        try {
          const refreshResponse = await apiClient.post('/auth/refresh-token', {}, {
            withCredentials: true,
            _skipCSRF: true
          });
          const { token: newAccessToken, csrfToken } = refreshResponse.data || {};

          if (newAccessToken) {
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
          }
          if (csrfToken) {
            sessionStorage.setItem('csrfToken', csrfToken);
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['X-CSRF-Token'] = csrfToken;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }

    // Refresh an expired access token once, then retry the original request.
    // This prevents normal page navigation from turning one expired-cookie request
    // into a visible session timeout or empty dashboard state.
    if (error.response && error.response.status === 401) {
      const errorCode = error.response?.data?.code;
      const canRetryAuth = originalRequest &&
        !originalRequest._retryAuth &&
        !originalRequest._skipAuthRefresh &&
        !originalRequest.url?.includes('/auth/refresh-token') &&
        errorCode === 'TOKEN_EXPIRED';

      if (canRetryAuth) {
        originalRequest._retryAuth = true;
        try {
          await refreshAccessToken();
          return apiClient(originalRequest);
        } catch (refreshError) {
          error = refreshError;
        }
      }
    }

    // Only trigger logout on 401 (Unauthorized), not on 403 (Forbidden - insufficient permissions)
    // 403 means user is authenticated but lacks permissions for the specific resource
    if (error.response && error.response.status === 401) {
      if (originalRequest?._skipAuthErrorHandler) {
        return Promise.reject(error);
      }

      // Store current URL before redirecting (for redirect after re-login)
      const currentPath = window.location.pathname + window.location.search;
      setRedirectAfterLogin(currentPath);
      
      // TC-AUTH-008: Check for concurrent session error code
      const errorCode = error.response?.data?.code;
      if (errorCode === 'CONCURRENT_SESSION') {
        // Store concurrent session flag for login page to display message
        localStorage.setItem('concurrentSessionDetected', 'true');
        localStorage.setItem('concurrentSessionMessage', error.response?.data?.message || 'Logged in from another device. Please log in again.');

        // Dispatch a custom event - AuthRouterWrapper.handleConcurrentSession handles
        // the redirect directly, so skip authErrorHandler to avoid showing the modal
        window.dispatchEvent(new CustomEvent('auth:concurrentSession', {
          detail: {
            message: error.response?.data?.message || 'Logged in from another device. Please log in again.',
            code: 'CONCURRENT_SESSION'
          }
        }));
        return Promise.reject(error);
      }

      // Auth-generation guard: ignore 401s from requests issued under an older auth generation
      // (e.g. stale in-flight requests from before a successful re-login). These must not re-open
      // the session-timeout modal after the user has already logged back in.
      const requestGeneration = originalRequest?.metadata?.authGeneration;
      if (requestGeneration !== undefined && requestGeneration < _authGeneration) {
        return Promise.reject(error);
      }

      // Single-trigger guard: prevent multiple simultaneous auth error handlers
      if (_authErrorFiring) return Promise.reject(error);
      _authErrorFiring = true;
      if (authErrorHandler) {
        authErrorHandler();
      }
      // Reset guard after a short delay so next auth error can fire again
      setTimeout(() => { _authErrorFiring = false; }, 1000);
    }
    // Note: 403 errors (Forbidden) are NOT handled here - they should be handled by the component
    // to show appropriate "insufficient permissions" messages without logging out the user

    return Promise.reject(error);
  }
);

// Single-trigger guard: prevent multiple simultaneous auth error handlers
let _authErrorFiring = false;

// Monotonic auth generation. Bumped on each successful login so 401s from requests issued under a
// previous session (stale in-flight, other-tab leftovers) are ignored instead of stacking a modal.
let _authGeneration = 0;
export const bumpAuthGeneration = () => { _authGeneration += 1; };

let notificationApi = null; // Variable to hold the context-aware notification API
let authErrorHandler = null; // Variable to hold the AuthContext's error handler

export const setNotificationApi = (apiInstance) => {
  notificationApi = apiInstance;
};

export const setAuthErrorHandler = (handler) => {
  authErrorHandler = handler;
};

export const setApiAccessToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// Authentication services
let isFetchingTaxpayerToken = false; // Flag to prevent multiple concurrent fetches

const authService = {
  /**
   * Get token as taxpayer using saved credentials
   * @returns {Promise} Token data
   */
  getTaxpayerToken: async () => {
    try {
      const response = await apiClient.post('/irb/taxpayer-token');
      return response;
    } catch (error) {
      logRethrow('Error getting taxpayer token:', error);
    }
  },

  /**
   * Get token as intermediary on behalf of a taxpayer
   * @param {string} tin - Taxpayer Identification Number
   * @returns {Promise} Token data
   */
  getIntermediaryToken: async (tin) => {
    try {
      const response = await apiClient.post('/irb/intermediary-token', { tin });
      return response;
    } catch (error) {
      logRethrow('Error getting intermediary token:', error);
    }
  },

  /**
   * Get user profile data
   * @returns {Promise} User profile data
   */
  getUserProfile: async () => {
    try {
      const response = await apiClient.get('/auth/profile');
      return response;
    } catch (error) {
      logRethrow('Error getting user profile:', error);
    }
  },

  /**
   * Upload letterhead image
   * @param {FormData} formData - FormData containing the letterhead file
   * @returns {Promise} Upload response data
   */
  uploadLetterhead: async (formData) => {
    try {
      const response = await apiClient.post('/auth/upload-letterhead', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      logRethrow('Error uploading letterhead:', error);
    }
  },

  /**
   * Delete letterhead image
   * @returns {Promise} Delete response data
   */
  deleteLetterhead: async () => {
    try {
      const response = await apiClient.delete('/auth/delete-letterhead');
      return response;
    } catch (error) {
      logRethrow('Error deleting letterhead:', error);
    }
  },

  // Remove client-side token storage and validation logic
  // getStoredIrbTokenInfo, getCurrentIrbToken, and hasValidIrbToken are no longer needed on the client
};

// Settings services
const settingsService = {
  /**
   * Test LHDN API connection
   * @param {Object} credentialsData - Credentials data to test
   * @returns {Promise} Test result
   */
  testConnection: (credentialsData) => apiClient.post('/settings/test-connection', credentialsData),

  /**
   * Save API credentials to the server
   * @param {Object} credentialsData - Credentials data
   * @returns {Promise} Response data
   */
  saveCredentials: (credentialsData) => apiClient.post('/settings/save-credentials', credentialsData),

  /**
   * Get credentials status for the current user
   * @returns {Promise} Status data
   */
  getCredentialsStatus: () => apiClient.get('/settings/credentials-status'),

  /**
   * Get token status for the current user
   * @returns {Promise} Status data
   */
  getTokenStatus: () => apiClient.get('/settings/token-status'),

  /**
   * Manually refresh the LHDN API token
   * @returns {Promise} Refresh result
   */
  refreshToken: () => apiClient.post('/settings/refresh-token'),

  /**
   * Clear/Delete API credentials for the current user
   * @returns {Promise} Response data
   */
  clearCredentials: () => apiClient.delete('/settings/credentials')
};

const irbService = {
  /**
   * Example: Get taxpayer information
   * @param {string} tin - Taxpayer identification number
   * @returns {Promise} Taxpayer data
   */
  getTaxpayerInfo: (tin) => apiClient.get(`/irb/taxpayers/${tin}`),

  /**
   * Example: Submit invoice (potentially a specific type or older version of submitDocuments)
   * @param {Object} invoiceData - Invoice data, likely a single invoice object
   * @returns {Promise} Response data
   */
  submitInvoice: (invoiceData) => apiClient.post('/irb/invoices', invoiceData),

  /**
   * Validate a taxpayer's TIN
   * @param {string} tin - Taxpayer identification number
   * @param {string} idType - Type of ID (NRIC, PASSPORT, BRN, ARMY)
   * @param {string} idValue - Value of the ID
   * @param {boolean} isIntermediary - Whether to use intermediary token
   * @returns {Promise} Validation result
   */
  validateTIN: async (tin, idType, idValue, isIntermediary = false) => {
    try {
      // Ensure a valid token exists before making the request
      const response = await apiClient.get(`/irb/validate-tin/${tin}`, {
        params: {
          idType,
          idValue,
          isIntermediary
        }
      });
      return response;
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  },

  /**
   * Search for a taxpayer's TIN using various search parameters
   * This API allows searching for TIN by taxpayer name or ID type+value
   * 
   * Supported search parameter combinations:
   * 1) Taxpayer Name only
   * 2) ID Type + ID Value
   * 3) ID Type + ID Value + File Type
   * 4) All parameters (uses AND operator)
   * 
   * @param {Object} searchParams - Search parameters
   * @param {string} [searchParams.taxpayerName] - The Taxpayer Name
   * @param {string} [searchParams.idType] - ID Type (NRIC, PASSPORT, BRN, ARMY)
   * @param {string} [searchParams.idValue] - The actual value of the ID Type
   * @param {string} [searchParams.fileType] - 1 for IG TIN (individual), 2 for non-IG TIN (non-individual)
   * @param {boolean} [isIntermediary=false] - Whether to use intermediary token
   * @returns {Promise} Search result containing TIN if found
   */
  searchTIN: async (searchParams, isIntermediary = false) => {
    try {
      const { taxpayerName, idType, idValue, fileType } = searchParams;
      
      // Build query parameters - only include defined values
      const params = {};
      if (taxpayerName) params.taxpayerName = taxpayerName;
      if (idType) params.idType = idType;
      if (idValue) params.idValue = idValue;
      if (fileType) params.fileType = fileType;
      if (isIntermediary) params.isIntermediary = isIntermediary;

      const response = await apiClient.get('/irb/search-taxpayer-tin', { params });
      return response;
    } catch (error) {
      console.error('TIN Search API Request failed:', error);
      throw error;
    }
  },

  /**
   * Get taxpayer information by QR code
   * This API allows retrieving full taxpayer details by scanning/decoding a QR code
   * 
   * @param {string} qrCodeText - The decoded QR code text (Base64 decoded UUID)
   * @param {boolean} [isIntermediary=false] - Whether to use intermediary token
   * @returns {Promise} Taxpayer information including TIN, name, address, etc.
   */
  getTaxpayerByQRCode: async (qrCodeText, isIntermediary = false) => {
    try {
      // Validate input
      if (!qrCodeText || qrCodeText.trim().length === 0) {
        throw new Error('QR code text is required');
      }

      const params = {};
      if (isIntermediary) params.isIntermediary = isIntermediary;

      const response = await apiClient.get(`/irb/taxpayer-qrcode/${encodeURIComponent(qrCodeText.trim())}`, { params });
      return response;
    } catch (error) {
      console.error('QR Code Lookup API Request failed:', error);
      throw error;
    }
  },

  /**
   * Submit one or more documents
   * @param {Object[]} documents - Array of document objects to submit
   * @returns {Promise} Submission result
   */
  submitDocuments: async (documents, options = {}) => {
    const { requestId } = options;
    try {
      const headers = requestId ? { 'X-Request-ID': requestId } : {};
      const response = await apiClient.post('/irb/documentsubmissions', documents, {
        timeout: 600000,
        headers,
      });
      return response;
    } catch (error) {
      logRethrow('Error submitting documents:', error);
    }
  },

  /**
   * Get submission details from the IRB API.
   * @param {string} submissionUid - Unique ID of the document submission to retrieve.
   * @param {Object} params - Query parameters for pagination (pageNo, pageSize).
   * @param {boolean} [isIntermediary=false] - Whether this call is made as an intermediary.
   * @param {string} [tin] - Taxpayer TIN (required if isIntermediary is true).
   * @returns {Promise<Object>} API response data containing submission details.
   */
  getSubmissionDetails: async (submissionUid, params = { pageNo: 1, pageSize: 10 }, isIntermediary = false, tin) => {
    try {
      // Ensure a valid token exists before making the request

      // Log the request for debugging

      // Make the API call through the backend service
      const response = await apiClient.get(`/irb/documentsubmissions/${submissionUid}`, {
        params: {
          pageNo: params.pageNo || 1,
          pageSize: params.pageSize || 10,
          source: params.source,
          isIntermediary: isIntermediary,
          tin: isIntermediary ? tin : undefined
        },
        ...(params.signal ? { signal: params.signal } : {})
      });

      return response.data;
    } catch (error) {
      console.error(`Error fetching submission details for submissionUid: ${submissionUid}:`, error.response?.data || error.message);
      throw error;
    }
  },

  submissionCoordinator: {
    subscribe: (submissionUid, handler, params = { pageNo: 1, pageSize: 100 }) => {
      let disposed = false;
      const handleUpdate = (payload) => {
        if (disposed || payload?.submissionUid !== submissionUid) return;
        handler({ status: payload.status || 'success', data: payload.data, error: payload.error || null });
      };

      websocketService.on('submission_status_update', handleUpdate);

      irbService.getSubmissionDetails(submissionUid, params)
        .then((snapshot) => {
          if (!disposed) handler(snapshot);
        })
        .catch((error) => {
          if (!disposed) handler({ status: 'error', error });
        });

      return () => {
        disposed = true;
        websocketService.off('submission_status_update', handleUpdate);
      };
    }
  },

  getNotifications: async (params = {}) => {
    try {
      // Ensure a valid app token is sent by apiClient's interceptor
      const response = await apiClient.get('/irb/notifications', { params });
      return response.data; // Assuming backend returns { status: 'success', data: ... } or throws error
    } catch (error) {
      const status = Number(error.response?.status || error.response?.data?.code);
      const log = [429, 502, 503, 504].includes(status) ? console.warn : console.error;
      log('Error fetching notifications via backend service:', error.response?.data || error.message);
      // The error will be handled by the component calling the service
      throw error;
    }
  },

  /**
   * Mark a notification as read
   * Simple implementation - backend just logs, frontend handles actual read status
   * @param {string} notificationId - The notification ID to mark as read
   * @returns {Promise<Object>} API response data
   */
  markNotificationAsRead: async (notificationId) => {
    try {
      const response = await apiClient.put(`/user/notifications/${notificationId}/mark-as-read`);
      return response.data; // Expecting { status: 'success', message: '...', data: { notificationId, read: true, readAt: '...' } }
    } catch (error) {
      // Non-critical operation: log warning but don't throw — caller should update local state
      console.warn(`[NotificationContext] markNotificationAsRead failed for ${notificationId}: ${error.message}`);
      return {
        status: 'error',
        message: error.response?.data?.message || error.message || 'Failed to mark notification as read',
        data: { notificationId, read: false }
      };
    }
  },

  /**
   * Mark all notifications as read
   * Simple implementation - backend just logs, frontend handles actual read status
   * @returns {Promise<Object>} API response data
   */
  markAllNotificationsAsRead: async () => {
    try {
      const response = await apiClient.put('/user/notifications/mark-all-as-read');
      return response.data; // Expecting { status: 'success', message: '...', data: { read: true, readAt: '...' } }
    } catch (error) {
      // Non-critical operation: log warning but don't throw — caller should update local state
      console.warn(`[NotificationContext] markAllNotificationsAsRead failed: ${error.message}`);
      return {
        status: 'error',
        message: error.response?.data?.message || error.message || 'Failed to mark all notifications as read',
        data: { read: false }
      };
    }
  },

  /**
   * Get recent documents from MyInvois API via backend.
   * @param {Object} params - Query parameters for the API.
   * @returns {Promise<Object>} API response data.
   */
  getRecentSubmittedDocuments: async (params = {}) => {
    try {
      const response = await apiClient.get('/v1.0/documents/recent', { params });
      return response.data; // Assuming backend returns { status: 'success', data: ... } or throws error
    } catch (error) {
      logRethrow('Error fetching recent submitted documents via backend service:', error);
    }
  },

  /**
   * Search documents using the MyInvois API via backend.
   * @param {Object} params - Query parameters for the API.
   * @returns {Promise<Object>} API response data.
   */
  searchDocuments: async (params = {}) => {
    try {
      const response = await apiClient.get('/v1.0/documents/search', { params });
      return response.data; // Assuming backend returns { status: 'success', data: ... } or throws error
    } catch (error) {
      logRethrow('Error searching documents via backend service:', error);
    }
  },

  /**
   * Get document details from MyInvois API via backend.
   * @param {string} uuid - The unique ID of the document.
   * @returns {Promise<Object>} API response data.
   */
  getSubmittedDocumentDetails: async (uuid) => {
    try {
      const response = await apiClient.get(`/irb/submitted-document-details/${uuid}`);
      return response.data; // Assuming backend returns { status: 'success', data: ... } or throws error
    } catch (error) {
      console.error(`Error fetching submitted document details for UUID ${uuid} via backend service:`, error.response?.data || error.message);
      throw error;
    }
  },

  getBatchDocumentErrorDetails: async (uuids) => {
    try {
      const response = await apiClient.get('/irb/batch-document-error-details', {
        params: { uuids: Array.isArray(uuids) ? uuids.join(',') : uuids }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching batch document error details:', error.response?.data || error.message);
      return { status: 'error', data: {} };
    }
  },

  /**
   * Get invoice form data from local database via backend.
   * @param {string} uuid - The unique ID or invoice number of the document.
   * @returns {Promise<Object>} API response data.
   */
  getInvoiceFormData: async (uuid) => {
    try {
      const response = await apiClient.get(`/irb/invoice-form-data/${uuid}`);
      return response.data; // Assuming backend returns { status: 'success', data: ... } or throws error
    } catch (error) {
      console.error(`Error fetching invoice form data for UUID ${uuid} via backend service:`, error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Cancel a document via MyInvois API via backend.
   * @param {string} uuid - The unique ID of the document.
   * @param {string} reason - The reason for cancellation.
   * @returns {Promise<Object>} API response data.
   */
  cancelDocument: async (uuid, reason) => {
    try {
      const response = await apiClient.put(`/v1.0/documents/state/${uuid}/state`, {
        status: 'cancelled',
        reason: reason
      }, {
        // Server can hold a cancel arbitrarily long: the details pre-check waits out the
        // shared LHDN rate-limit cooldown before the paced cancel even runs. No client
        // timeout — server + bulk runner enforce pacing, already-cancelled maps to success.
        timeout: 0
      });
      return response.data; // Assuming backend returns { status: 'success', data: ... } or throws error
    } catch (error) {
      console.error(`Error cancelling document ${uuid} via backend service:`, error.response?.data || error.message);
      throw error;
    }
  },



  /**
   * Get raw document content (XML or JSON) from the IRB API.
   * @param {string} uuid - Unique ID of the document to retrieve.
   * @param {string} format - Desired format ('xml' or 'json').
   * @returns {Promise<Object>} API response data containing the raw document.
   */
  getRawDocument: async (uuid, format) => {
    try {
      // Ensure a valid token exists before making the request

      // Determine the Accept header based on the requested format
      const acceptHeader = format === 'xml' ? 'application/xml' : 'application/json';

      // Make the API call through the backend service
      const response = await apiClient.get(`/v1.0/documents/${uuid}/raw`, {
        headers: {
          'Accept': acceptHeader
        },
        // Set responseType to 'blob' to handle binary data (like files)
        responseType: 'blob'
      });

      return response;
    } catch (error) {
      console.error(`Error fetching raw document for UUID: ${uuid}:`, error.response?.data || error.message);
      throw error;
    }
  },
  
  /**
   * Generate the public URL for a document.
   * @param {string} uuid - The unique ID of the document.
   * @param {string} longId - The long ID for sharing.
   * @param {string} baseUrl - The base URL for the MyInvois portal.
   * @returns {string} The public document URL.
   */
  generatePublicDocumentUrl: (uuid, longId, baseUrl) => {
    if (!baseUrl) {
      console.error('Base URL for public document URL is not provided.');
      // Return a placeholder or throw an error as appropriate for your application
      return '#'; // Or throw new Error('Base URL missing');
    }
    return `${baseUrl}/${uuid}/share/${longId}`;
  },
};

// Invoice CRUD services (for drafts)
const invoiceService = {
  /**
   * Save draft invoice with retry logic for timeout handling
   * CRITICAL FIX: Added retry logic to handle network timeouts on slow connections
   * @param {Object} payload - Invoice data
   * @param {number} maxRetries - Maximum retry attempts (default: 3)
   * @returns {Promise} Response data
   */
  saveDraft: async (payload, maxRetries = 3) => {
    try {
      return await retryWithBackoff(async () => {
        const response = await apiClient.post('/invoices', payload, {
          timeout: 60000
        });
        return response;
      }, maxRetries);
    } catch (error) {
      logRethrow('Error saving draft invoice (all retries exhausted):', error);
    }
  },

  /**
   * Update draft invoice with retry logic for timeout handling
   * CRITICAL FIX: Added retry logic to handle network timeouts on slow connections
   * @param {string} invoiceId - Invoice ID
   * @param {Object} payload - Invoice data
   * @param {number} maxRetries - Maximum retry attempts (default: 3)
   * @returns {Promise} Response data
   */
  updateDraft: async (invoiceId, data, attempt = 1) => {
    try {
      return await apiClient.put(`/invoices/${invoiceId}`, data, {
        timeout: 60000
      });
    } catch (error) {
      if (error.response?.data?.code === 'VERSION_ERROR' && attempt < 3) {
        const delay = Math.pow(2, attempt) * 500;
        await new Promise(r => setTimeout(r, delay));
        return invoiceService.updateDraft(invoiceId, data, attempt + 1);
      }
      console.error('Error updating draft invoice:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Lightweight version probe - returns { version, sourceDataHash, updatedAt } only (never the
   * full doc). Used to resync the editor's known version after a save whose response was lost to
   * a timeout, so the next save does not send a stale version and trip a false version conflict.
   */
  getDraftVersion: async (invoiceId) => {
    const response = await apiClient.get(`/invoices/${invoiceId}/version`, { timeout: 10000 });
    return response.data;
  },
  /**
   * Get invoices with retry logic for timeout handling
   * CRITICAL FIX: Added retry logic to handle network timeouts on slow connections
   * @param {Object} params - Query parameters (status, page, limit, etc.)
   * @param {number} maxRetries - Maximum retry attempts (default: 3)
   * @returns {Promise} Response data
   */
  getMySubmittedInvoices: async (params = {}) => {
    try {
      const { signal, ...queryParams } = params;
      const response = await apiClient.get('/invoices/my-submitted', {
        params: queryParams,
        ...(signal ? { signal } : {}),
        timeout: 30000
      });
      return response.data;
    } catch (error) {
      logRethrow('Error fetching my submitted invoices:', error);
    }
  },
  // Buyer-invoice email status map plus the in-process dispatcher health snapshot.
  getBuyerEmailStatus: async (uuids) => {
    try {
      const list = Array.isArray(uuids) ? uuids : [uuids];
      const clean = list.filter(Boolean);
      if (!clean.length) return { statuses: {}, worker: null };
      const response = await apiClient.get('/invoices/email-status', { params: { uuids: clean.join(',') } });
      return { statuses: response.data?.data || {}, worker: response.data?.worker || null };
    } catch (error) {
      logRethrow('Error fetching buyer email status:', error);
    }
  },
  // Re-queue the buyer-invoice email; optionally set the buyer email when the invoice has none. Throws on 4xx so the UI shows the message.
  resendBuyerEmail: async (invoiceId, buyerEmail, opts = {}) => {
    const body = {};
    if (buyerEmail) body.buyerEmail = buyerEmail;
    if (opts.ccOnly) body.ccOnly = true; // "Send to 2nd buyer": target this address only, don't overwrite the record
    const response = await apiClient.post(`/invoices/${invoiceId}/resend-email`, body);
    return response.data;
  },
  // Bulk re-queue buyer emails by uuid. Returns { uuid: { status:'queued' } | { status:'skipped', reason } }. Throws on 4xx.
  resendBuyerEmails: async (uuids, opts = {}) => {
    const clean = (Array.isArray(uuids) ? uuids : [uuids]).filter(Boolean);
    const body = { uuids: clean };
    if (opts.force) body.force = true; // deliberate resend of already-sent invoices
    const response = await apiClient.post('/invoices/resend-emails', body);
    return response.data?.data || {};
  },
  getInvoices: async (params = {}) => {
    try {
      const { requestTimeout, skipRequestManager, signal: requestSignal, ...queryParams } = params;
      const fetchInvoices = (signal) => (
        apiClient.get('/invoices', {
          params: queryParams,
          ...(signal ? { signal } : {}),
          timeout: requestTimeout || 30000
        })
      );
      const response = skipRequestManager
        ? await fetchInvoices(requestSignal)
        : await invoiceRequestManager.request(queryParams, fetchInvoices);
      return response.data;
    } catch (error) {
      if (!isInvoiceRequestCanceled(error)) {
        console.error('Error fetching invoices:', error.response?.data || error.message);
      }
      throw error;
    }
  },
  // Batch lookup: given a page of invoice numbers, returns { base -> [family members] } so the UI
  // can show whether a rejected/invalid invoice was already resubmitted as a -NNN correction.
  getCorrectionFamilies: async (numbers = []) => {
    if (!Array.isArray(numbers) || numbers.length === 0) return {};
    try {
      const response = await apiClient.post('/invoices/correction-families', { numbers });
      return response.data?.data || {};
    } catch (error) {
      console.error('Error fetching correction families:', error.response?.data || error.message);
      return {};
    }
  },
  // Batch lookup: { [invoiceId]: sizeBytes } for the submission queue's Total Size + 5MB cap.
  getInvoiceSizes: async (ids = []) => {
    if (!Array.isArray(ids) || ids.length === 0) return {};
    try {
      const response = await apiClient.post('/invoices/sizes', { ids });
      return response.data?.data || {};
    } catch (error) {
      console.error('Error fetching invoice sizes:', error.response?.data || error.message);
      return {};
    }
  },
  getInvoicesForExport: async (params = {}) => {
    const requestStartedAt = Date.now();
    const diagnosticMode = isDevelopment
      || params.debugExport === 1
      || params.debugExport === '1'
      || params.debugExport === true;
    try {
      const { signal, requestTimeout, ...queryParams } = params;
      if (diagnosticMode) {
        console.info('[INVOICE_EXPORT_API] Request started', {
          exportRunId: queryParams.exportRunId || null,
          requestId: queryParams.exportRequestId || null,
          chunk: queryParams.exportChunk || queryParams.page || 1,
          attempt: queryParams.exportAttempt || 1,
          limit: queryParams.limit || null
        });
      }
      const response = await apiClient.get('/invoices/export', {
        params: queryParams,
        ...(signal ? { signal } : {}),
        timeout: requestTimeout ?? 0,
        _disableAutoRetry: true
      });
      if (diagnosticMode) {
        console.info('[INVOICE_EXPORT_API] Request completed', {
          exportRunId: queryParams.exportRunId || null,
          requestId: queryParams.exportRequestId || null,
          chunk: queryParams.exportChunk || queryParams.page || 1,
          attempt: queryParams.exportAttempt || 1,
          durationMs: Date.now() - requestStartedAt
        });
      }
      return response.data;
    } catch (error) {
      if (diagnosticMode) {
        console.warn('[INVOICE_EXPORT_API] Request failed', {
          exportRunId: params.exportRunId || null,
          requestId: params.exportRequestId || null,
          chunk: params.exportChunk || params.page || 1,
          attempt: params.exportAttempt || 1,
          errorCode: error.response?.data?.code || error.code || null,
          errorClass: error.response?.data?.errorClass || null,
          durationMs: Date.now() - requestStartedAt
        });
      }
      if (error.code !== 'ERR_CANCELED') {
        console.error('Error fetching invoice export data:', error.response?.data || error.message);
      }
      throw error;
    }
  },
  createInvoiceExportJob: async ({ filters = {}, sort = {}, signal } = {}) => {
    const response = await apiClient.post('/invoices/export-jobs', { filters, sort }, {
      ...(signal ? { signal } : {}),
      timeout: 30000,
      _disableAutoRetry: true
    });
    return response.data;
  },
  getInvoiceExportJob: async (jobId, { signal } = {}) => {
    const response = await apiClient.get(`/invoices/export-jobs/${jobId}`, {
      ...(signal ? { signal } : {}),
      params: { _t: Date.now() },
      timeout: 30000,
      _disableAutoRetry: true
    });
    return response.data;
  },
  downloadInvoiceExportJob: async (jobId, { signal } = {}) => {
    const response = await apiClient.get(`/invoices/export-jobs/${jobId}/download`, {
      ...(signal ? { signal } : {}),
      responseType: 'blob',
      timeout: 0,
      _disableAutoRetry: true
    });
    return response.data;
  },
  cancelInvoiceExportJob: async (jobId) => {
    const response = await apiClient.post(`/invoices/export-jobs/${jobId}/cancel`, {}, {
      timeout: 30000,
      _disableAutoRetry: true
    });
    return response.data;
  },
  cancelInvoiceRequests: () => invoiceRequestManager.cancel(),
  isInvoiceRequestCanceled,
  getInvoiceStats: async (params = {}, maxRetries = 3) => {
    try {
      return await retryWithBackoff(async () => {
        const response = await apiClient.get('/invoices/stats', {
          params,
          timeout: 30000
        });
        return response.data;
      }, maxRetries);
    } catch (error) {
      logRethrow('Error fetching invoice stats (all retries exhausted):', error);
    }
  },
  getInvoiceById: async (invoiceId, options = {}) => {
    try {
      const { signal, timeout = 30000, disableAutoRetry = false } = options;
      const response = await apiClient.get(`/invoices/${invoiceId}`, {
        ...(signal ? { signal } : {}),
        timeout,
        ...(disableAutoRetry ? { _disableAutoRetry: true } : {}),
      });
      return response.data; // Expecting { status: 'success', data: invoice }
    } catch (error) {
      logRethrow('Error fetching invoice by ID:', error);
    }
  },
  /**
   * Delete draft invoice with retry logic for timeout handling
   * CRITICAL FIX: Added retry logic to handle network timeouts on slow connections
   * @param {string} invoiceId - Invoice ID to delete
   * @param {number} maxRetries - Maximum retry attempts (default: 3)
   * @returns {Promise} Response data
   */
  deleteDraft: async (invoiceId, maxRetries = 3) => {
    try {
      return await retryWithBackoff(async () => {
        const response = await apiClient.delete(`/invoices/${invoiceId}`, {
          timeout: 60000
        });
        return response;
      }, maxRetries);
    } catch (error) {
      logRethrow('Error deleting draft invoice (all retries exhausted):', error);
    }
  },
  /**
   * Duplicate draft invoice with retry logic for timeout handling
   * CRITICAL FIX: Added retry logic to handle network timeouts on slow connections
   * @param {string} invoiceId - Invoice ID to duplicate
   * @param {number} maxRetries - Maximum retry attempts (default: 3)
   * @returns {Promise} Response data
   */
  duplicateDraft: async (invoiceId, maxRetries = 3) => {
    try {
      return await retryWithBackoff(async () => {
        const response = await apiClient.post(`/invoices/duplicate/${invoiceId}`, null, {
          timeout: 60000
        });
        return response.data;
      }, maxRetries);
    } catch (error) {
      logRethrow('Error duplicating draft invoice (all retries exhausted):', error);
    }
  },
  getInvoiceIds: async (params = {}) => {
    try {
      const response = await apiClient.get('/invoices/ids', { params });
      return response.data;
    } catch (error) {
      logRethrow('Error fetching invoice IDs:', error);
    }
  },

  checkInvoiceExistence: async (invoiceNumbers) => {
    try {
      const response = await apiClient.post('/invoices/check-existence', { invoiceNumbers });
      return response.data;
    } catch (error) {
      logRethrow('Error checking invoice existence:', error);
    }
  },

  createImportJob: (payload) => apiClient.post('/invoices/import-jobs', payload, {
    timeout: 60000
  }),

  getImportJob: (jobId) => apiClient.get(`/invoices/import-jobs/${jobId}`, {
    timeout: 30000
  }),

  getLatestImportJob: () => apiClient.get('/invoices/import-jobs/latest-active', {
    timeout: 30000
  }),

  resumeImportJob: (jobId) => apiClient.post(`/invoices/import-jobs/${jobId}/resume`, {}, {
    timeout: 30000
  }),

  getUnifiedImportStatus: (importId) => apiClient.get(`/import-status/${importId}`, {
    timeout: 30000
  }),

  getLatestUnifiedImportStatus: (type = null) => apiClient.get('/import-status/latest-active', {
    params: type ? { type } : undefined,
    timeout: 30000
  }),

  batchDelete: async (ids, maxRetries = 3) => {
    try {
      return await retryWithBackoff(async () => {
        const response = await apiClient.post('/invoices/batch-delete', { ids }, { timeout: 120000 });
        return response.data;
      }, maxRetries);
    } catch (error) {
      logRethrow('Error batch-deleting invoices (all retries exhausted):', error);
    }
  },

  batchDuplicate: async (ids, maxRetries = 3) => {
    try {
      return await retryWithBackoff(async () => {
        const response = await apiClient.post('/invoices/batch-duplicate', { ids }, { timeout: 120000 });
        return response.data;
      }, maxRetries);
    } catch (error) {
      logRethrow('Error batch-duplicating invoices (all retries exhausted):', error);
    }
  },

  convertExternalData: async (externalData) => {
    try {
      const response = await apiClient.post('/invoices/convert-external', { externalData });
      return response.data; // Expecting { status: 'success', data: invoiceData }
    } catch (error) {
      logRethrow('Error converting external data:', error);
    }
  },


  getAutoImportHistory: async ({ page = 1, limit = 20 } = {}) => {
    const response = await apiClient.get('/invoices/service/auto-import-history', { params: { page, limit } });
    return response.data;
  },

  getAutoImportStatus: async (signal = null) => {
    try {
      const response = await apiClient.get('/api/health/auto-import', signal ? { signal } : {});
      
      // Standardized format from backend: { status: 'success', data: { isHealthy, isConnected, isRunning, ... } }
      if (response && response.data && response.data.status === 'success' && response.data.data) {
        const healthData = response.data.data;
        
        
        // Transform health data to match expected format in autoImportSlice
        return {
          status: 'success',
          data: {
            isConnected: healthData.isConnected,
            isRunning: healthData.isRunning,
            isHealthy: healthData.isHealthy,
            serviceStatus: healthData.serviceStatus,
            message: healthData.message,
            lastActivity: healthData.lastHeartbeat,
            secondsSinceLastActivity: healthData.secondsSinceLastHeartbeat,
            uptime: healthData.uptime,
            pid: healthData.pid,
            progress: healthData.progress,
            importSessionId: healthData.importSessionId || healthData.jobId || null,
            jobId: healthData.jobId || healthData.importSessionId || null,
            config: healthData.config,
            totalInvoices: healthData.totalInvoices || 0,
            processedCount: healthData.processedCount || 0,
            successCount: healthData.successCount || 0,
            failedCount: healthData.failedCount || 0,
            createdCount: healthData.createdCount || 0,
            updatedCount: healthData.updatedCount || 0,
            unchangedCount: healthData.unchangedCount || 0,
            skippedCount: healthData.skippedCount || 0,
            skippedReason: healthData.skippedReason || null,
            skippedReasonMessage: healthData.skippedReasonMessage || null,
            protectedCount: healthData.protectedCount || 0,
            conflictCount: healthData.conflictCount || 0,
            sourceChangedCount: healthData.sourceChangedCount || 0,
            validationFailedCount: healthData.validationFailedCount || 0,
            failedRecords: healthData.failedRecords || [],
            failedInvoices: healthData.failedInvoices || [],
            results: healthData.results || [],
            lastImportResult: healthData.lastImportResult || null,
            unifiedImportContext: healthData.unifiedImportContext || null,
            timestamp: healthData.timestamp || null
          }
        };
      } else {
        console.error('[apiService] Health endpoint returned unexpected format:', response.data);
        throw new Error('Invalid response structure from health endpoint');
      }
    } catch (error) {
      console.error('Error getting auto-import status:', error.response?.data || error.message);
      
      // Provide more specific error messages for auto-import status
      if (error.response?.status === 401) {
        throw new Error('Authentication required for auto-import status. Please log in again.');
      } else if (error.response?.status === 404) {
        // Endpoint not found, service might be down
        return {
          status: 'success',
          data: {
            isConnected: false,
            isRunning: false,
            isHealthy: false,
            message: 'Auto-import service endpoint not available',
            serviceStatus: 'unknown'
          }
        };
      } else if (error.response?.status === 500) {
        throw new Error('Server error retrieving auto-import status. Please check the backend service.');
      } else if (error.message?.includes('Network Error')) {
        throw new Error('Network error retrieving auto-import status. Please check your internet connection.');
      } else {
        throw error;
      }
    }
  },
  triggerAutoImport: async (triggerSource = 'manual') => {
    try {
      const response = await apiClient.post('/invoices/trigger-auto-import', { triggerSource });
      return response.data; // Expecting { status: 'success', message: 'Import triggered' }
    } catch (error) {
      logRethrow('Error triggering auto-import:', error);
    }
  },
  testConnection: async () => {
    try {
      const response = await apiClient.post('/invoices/service/auto-import-test-connection');
      return response.data;
    } catch (error) {
      logRethrow('Error testing connection:', error);
    }
  },
  clearLogs: async (keepLines = 1000) => {
    try {
      const response = await apiClient.post('/invoices/service/auto-import-clear-logs', { keepLines });
      return response.data;
    } catch (error) {
      logRethrow('Error clearing logs:', error);
    }
  },

  // NEW: Clear manual trigger flag after frontend acknowledges completion
  clearManualTrigger: async (targetUsernames = null) => {
    try {
      const response = await apiClient.post('/invoices/service/auto-import-clear-trigger', { targetUsernames });
      return response.data;
    } catch (error) {
      logRethrow('Error clearing manual trigger:', error);
    }
  },

  stopAutoImportService: async () => {
    try {
      const response = await apiClient.post('/invoices/stop-auto-import-service');
      return response.data; // Expecting { status: 'success', message: 'Service stop command sent' }
    } catch (error) {
      logRethrow('Error stopping auto-import service:', error);
    }
  },

  acknowledgeSourceChange: async (invoiceId) => {
    try {
      const response = await apiClient.patch(`/invoices/${invoiceId}/acknowledge-change`);
      return response.data;
    } catch (error) {
      logRethrow('Error acknowledging source change:', error);
    }
  },

  createAmendment: async (invoiceId, data) => {
    try {
      const response = await apiClient.post(`/invoices/${invoiceId}/create-amendment`, data);
      return response.data;
    } catch (error) {
      logRethrow('Error creating amendment:', error);
    }
  },

  copyAsDraft: async (invoiceId) => {
    try {
      const response = await apiClient.post(`/invoices/${invoiceId}/copy-as-draft`);
      return response.data;
    } catch (error) {
      logRethrow('Error copying invoice as draft:', error);
    }
  },

  getServiceConfig: async () => {
    try {
      const response = await apiClient.get('/invoices/service/auto-import-config');
      return response.data;
    } catch (error) {
      logRethrow('Error getting service config:', error);
    }
  },
  getDashboardStats: async (force = false, clientTin = null, signal = null) => {
    try {
      const params = force ? {
        force: 'true',
        _t: Date.now()
      } : {};

      if (clientTin) {
        params.clientTin = clientTin;
      }

      const response = await apiClient.get('/invoices/dashboard-stats', { params, ...(signal ? { signal } : {}) });
      return response.data; // Expecting { status: 'success', data: stats }
    } catch (error) {
      const isCanceled = error.name === 'CanceledError' ||
        error.name === 'AbortError' ||
        error.message?.includes('cancel') ||
        error.message?.includes('aborted');
      const log = isCanceled ? console.warn : console.error;
      log('Error getting dashboard stats:', error.response?.data || error.message);
      throw error;
    }
  },

  // Recovery services
  checkMissingInvoices: async (targetUsername = null) => {
    try {
      const response = await apiClient.post('/invoices/check-missing', { targetUsername });
      return response.data; // Expecting { status: 'success', data: missingInvoices, totalMissing, message }
    } catch (error) {
      logRethrow('Error checking missing invoices:', error);
    }
  },

  recoverDeletedData: async (targetUsername = null, forceRecovery = false) => {
    try {
      const response = await apiClient.post('/invoices/recover-deleted', { 
        targetUsername, 
        forceRecovery 
      });
      return response.data; // Expecting { status: 'success', message, data: { recoveredCount, errorCount, totalMissing, results } }
    } catch (error) {
      logRethrow('Error recovering deleted data:', error);
    }
  },

  smartRecovery: async (targetUsername = null, forceRecovery = false, checkOnly = false) => {
    try {
      const response = await apiClient.post('/invoices/smart-recovery', { 
        targetUsername, 
        forceRecovery,
        checkOnly
      });
      return response.data; // Expecting { status: 'success', message, data: { missingCount, recoveredCount, actions, successRate } }
    } catch (error) {
      logRethrow('Error performing smart recovery:', error);
    }
  },

  recoverSpecificDeletedInvoice: async ({ invoiceNumber }) => {
    try {
      const response = await apiClient.post('/invoices/recover-specific-deleted', { invoiceNumber });
      return response.data; // Expecting { status: 'success', message, data: recoveredInvoice }
    } catch (error) {
      logRethrow('Error recovering specific deleted invoice:', error);
    }
  },

  // Submission History API methods
  /**
   * Get submission history with pagination
   * @param {Object} params - Query parameters (page, limit, status, dateFrom, dateTo)
   * @returns {Promise} Submission history data with pagination
   */
  getSubmissionHistory: async (params = {}) => {
    try {
      const { signal, ...queryParams } = params;
      const response = await apiClient.get('/invoices/submission-history', {
        params: queryParams,
        ...(signal ? { signal } : {})
      });
      return response.data; // Expecting { status: 'success', data: history[], pagination: {} }
    } catch (error) {
      logRethrow('Error fetching submission history:', error);
    }
  },

  getDegradedSubmissionHistory: async (params = {}) => {
    try {
      const response = await apiClient.get('/invoices/submission-history', {
        params: { problem: 'degraded', ...params }
      });
      return response.data;
    } catch (error) {
      logRethrow('Error fetching degraded submission history:', error);
    }
  },

  /**
   * Get specific submission history details
   * @param {string} historyId - The history entry ID
   * @returns {Promise} Detailed submission history entry
   */
  getSubmissionHistoryDetails: async (historyId) => {
    try {
      const response = await apiClient.get(`/invoices/submission-history/${historyId}`);
      return response.data; // Expecting { status: 'success', data: historyEntry }
    } catch (error) {
      logRethrow('Error fetching submission history details:', error);
    }
  },

  /**
   * Delete a specific submission history entry (soft delete)
   * @param {string} historyId - The history entry ID to delete
   * @returns {Promise} Delete result
   */
  deleteSubmissionHistory: async (historyId) => {
    try {
      const response = await apiClient.delete(`/invoices/submission-history/${historyId}`);
      return response.data; // Expecting { status: 'success', message: '...' }
    } catch (error) {
      logRethrow('Error deleting submission history:', error);
    }
  },

  /**
   * Clear all submission history for the user (soft delete all)
   * @returns {Promise} Clear result with count of cleared entries
   */
  clearSubmissionHistory: async () => {
    try {
      const response = await apiClient.delete('/invoices/submission-history');
      return response.data; // Expecting { status: 'success', message: '...', clearedCount: N }
    } catch (error) {
      logRethrow('Error clearing submission history:', error);
    }
  },

  getDbHealthDiagnostics: async (params = {}) => {
    try {
      const response = await apiClient.get('/invoices/diagnostics/db-health', { params });
      return response.data;
    } catch (error) {
      logRethrow('Error fetching DB health diagnostics:', error);
    }
  }

};

// PDF Generation services (simplified - direct generation only)
const pdfService = {
  /**
   * Generate PDF directly without queue
   * @param {string} invoiceId - The invoice ID
   * @returns {Promise} PDF blob
   */
  generateDirectPdf: async (invoiceId) => {
    try {
      const response = await apiClient.get(`/pdf/direct/${invoiceId}`, {
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error generating PDF:', error.response?.data || error.message);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to generate PDF'
      };
    }
  }
};

// Audit Log services (admin only)
const auditService = {
  /**
   * Get audit logs with filtering
   * @param {Object} params - Query parameters
   * @returns {Promise} Logs response
   */
  getLogs: async (params = {}) => {
    try {
      const response = await apiClient.get('/audit/logs', { params });
      return response.data;
    } catch (error) {
      logRethrow('Error fetching audit logs:', error);
    }
  },

  /**
   * Get audit statistics
   * @param {Object} params - Query parameters
   * @returns {Promise} Stats response
   */
  getStats: async (params = {}) => {
    try {
      const response = await apiClient.get('/audit/stats', { params });
      return response.data;
    } catch (error) {
      logRethrow('Error fetching audit stats:', error);
    }
  },

  /**
   * Get security alerts
   * @param {Object} params - Query parameters
   * @returns {Promise} Alerts response
   */
  getSecurityAlerts: async (params = {}) => {
    try {
      const response = await apiClient.get('/audit/security-alerts', { params });
      return response.data;
    } catch (error) {
      logRethrow('Error fetching security alerts:', error);
    }
  },

  /**
   * Get user activity
   * @param {string} userId - User ID
   * @param {Object} params - Query parameters
   * @returns {Promise} Activity response
   */
  getUserActivity: async (userId, params = {}) => {
    try {
      const response = await apiClient.get(`/audit/user-activity/${userId}`, { params });
      return response.data;
    } catch (error) {
      logRethrow('Error fetching user activity:', error);
    }
  },

  /**
   * Get login attempts
   * @param {Object} params - Query parameters
   * @returns {Promise} Login attempts response
   */
  getLoginAttempts: async (params = {}) => {
    try {
      const response = await apiClient.get('/audit/login-attempts', { params });
      return response.data;
    } catch (error) {
      logRethrow('Error fetching login attempts:', error);
    }
  }
};


const apiService = {
  api: apiClient, // Add the axios instance for direct API calls
  auth: authService,
  settings: settingsService,
  irb: irbService,
  submissionCoordinator: irbService.submissionCoordinator,
  invoices: invoiceService,
  pdf: pdfService,
  audit: auditService,
  user: {
    getNotificationPreferences: () => apiClient.get('/user/notification-preferences').then(r => r.data),
    updateNotificationPreferences: (prefs) => apiClient.put('/user/notification-preferences', prefs).then(r => r.data)
  },

  // System status
  system: {
    getPublicHealth: () => apiClient.get('/api/health').catch(() => ({ 
      data: { 
        status: 'down',
        services: {
          mongodb: { status: 'unknown', connected: false },
          redis: { status: 'unknown', connected: false }
        }
      } 
    })),
    getUptimeStatus: () => apiClient.get('/system/uptime-status').catch(() => ({
      data: {
        status: 'error',
        data: {
          monitoring: { enabled: false },
          health: { uptime: { formatted: 'N/A' }, services: { mongodb: { healthy: false }, redis: { healthy: false } } },
          uptimePercentage: 0
        }
      }
    })),
    // Version
    getVersion: () => apiClient.get('/system/version'),
  },

  // Maintenance Mode API
  getMaintenanceStatus: () => {
    return apiClient.get('/system/maintenance/status').catch(err => { console.error('API call failed:', err); throw err; });
  },
  getMaintenanceBypassToken: () => apiClient.get('/system/maintenance/bypass-token').catch(err => { console.error('API call failed:', err); throw err; }),
  enableMaintenance: (reason, estimatedDuration = 30) => apiClient.post('/system/maintenance/enable', { reason, estimatedDuration }).catch(err => { console.error('API call failed:', err); throw err; }),
  disableMaintenance: () => apiClient.post('/system/maintenance/disable').catch(err => { console.error('API call failed:', err); throw err; }),
  getMaintenanceLogs: (params = {}) => apiClient.get('/system/maintenance/logs', { params }).catch(err => { console.error('API call failed:', err); throw err; }),
  
  // Clear maintenance bypass token from localStorage
  clearMaintenanceBypassToken: () => {
    localStorage.removeItem('maintenanceBypassToken');
  },


  // PDF API (convenience methods)
  pdf: {
    // Queue PDF generation for invoice
    generate: (invoiceId, options = {}) => apiClient.post('/queue/pdf/generate', {
      invoiceId,
      ...options
    }).catch(err => { console.error('API call failed:', err); throw err; }),

    // Get PDF generation status
    getStatus: (jobId) => apiClient.get(`/queue/jobs/pdf-generation/${jobId}`).catch(err => { console.error('API call failed:', err); throw err; }),

    // Generate PDF directly without queue (fallback for Redis rate limit)
    generateDirectPdf: async (invoiceId) => {
      try {
        const response = await apiClient.get(`/pdf/direct/${invoiceId}`, {
          responseType: 'blob'
        });
        return {
          success: true,
          data: response.data
        };
      } catch (error) {
        console.error('Error generating direct PDF:', error.response?.data || error.message);
        return {
          success: false,
          message: error.response?.data?.message || error.message || 'Failed to generate PDF directly'
        };
      }
    }
  },

  // Invoice Submission API (convenience methods)
  invoiceSubmit: {
    // Queue invoice submission
    submit: (data) => apiClient.post('/queue/invoice/submit', data).catch(err => { console.error('API call failed:', err); throw err; }),

    // Get submission status
    getStatus: (jobId) => apiClient.get(`/queue/jobs/invoice-submission/${jobId}`).catch(err => { console.error('API call failed:', err); throw err; }),

    // Retry failed submission
    retry: (jobId) => apiClient.post(`/queue/jobs/invoice-submission/${jobId}/retry`).catch(err => { console.error('API call failed:', err); throw err; }),
  },

  // Email API
  email: {
    // Queue email notification
    send: (data) => apiClient.post('/queue/email/send', data).catch(err => { console.error('API call failed:', err); throw err; }),
  },

  // Document Processing API
  document: {
    // Queue document processing
    process: (data) => apiClient.post('/queue/document/process', data).catch(err => { console.error('API call failed:', err); throw err; }),
  },

  // LHDN Sync API
  lhdnSync: {
    syncWithLhdn: (params) => apiClient.post('/invoices/sync-with-lhdn', {}, { params }).catch(err => { console.error('Sync error:', err); throw err; }),
  },

  // LHDN Search Documents API - directly fetches from MyInvois API
  lhdnSearch: {
    /**
     * Search documents from LHDN MyInvois API
     * @param {Object} params - Search parameters
     * @param {string} params.submissionDateFrom - Start date (ISO format)
     * @param {string} params.submissionDateTo - End date (ISO format)
     * @param {string} params.issueDateFrom - Issue date start (optional)
     * @param {string} params.issueDateTo - Issue date end (optional)
     * @param {number} params.pageNo - Page number (default: 1)
     * @param {number} params.pageSize - Page size (default: 100)
     * @param {string} params.status - Status filter (Valid, Invalid, Cancelled, Submitted)
     * @param {string} params.invoiceDirection - Direction (Sent, Received)
     * @param {string} params.documentType - Document type (01=Invoice, etc.)
     * @returns {Promise} Search results
     */
    search: async (params = {}) => {
      try {
        const response = await apiClient.get('/v1.0/documents/search', {
          params: {
            submissionDateFrom: params.submissionDateFrom,
            submissionDateTo: params.submissionDateTo,
            issueDateFrom: params.issueDateFrom,
            issueDateTo: params.issueDateTo,
            pageNo: params.pageNo || 1,
            pageSize: Math.min(params.pageSize || 100, 100),
            status: params.status || undefined,
            invoiceDirection: params.invoiceDirection || undefined,
            documentType: params.documentType || undefined,
            searchQuery: params.searchQuery || undefined,
          }
        });
        return response.data;
      } catch (error) {
        logRethrow('LHDN Search error:', error);
      }
    },
  },

  // Invoice Import API
  import: {
    // Queue invoice import
    queue: (data) => apiClient.post('/queue/invoice/import', data).catch(err => { console.error('API call failed:', err); throw err; }),
  },

  // Analytics API
  analytics: {
    // Queue analytics job
    queue: (data) => apiClient.post('/queue/analytics', data).catch(err => { console.error('API call failed:', err); throw err; }),
  },

  // Cleanup API - Database Cleanup Management
  cleanup: {
    // Queue cleanup job (legacy)
    queue: (data) => apiClient.post('/queue/cleanup', data),

    /**
     * Get all collections with statistics
     * @returns {Promise} Collections data
     */
    getCollections: async () => {
      try {
        const response = await apiClient.get('/cleanup/collections');
        return response.data;
      } catch (error) {
        logRethrow('Error fetching collections:', error);
      }
    },

    /**
     * Get database statistics
     * @returns {Promise} Database stats
     */
    getStats: async () => {
      try {
        const response = await apiClient.get('/cleanup/stats');
        return response.data;
      } catch (error) {
        logRethrow('Error fetching DB stats:', error);
      }
    },

    /**
     * Get audit log statistics
     * @param {number} days - Number of days to analyze
     * @returns {Promise} Audit log stats
     */
    getAuditStats: async (days = 30) => {
      try {
        const response = await apiClient.get(`/cleanup/audit-logs/stats?days=${days}`);
        return response.data;
      } catch (error) {
        logRethrow('Error fetching audit stats:', error);
      }
    },

    /**
     * Get cleanup preview
     * @returns {Promise} Cleanup preview data
     */
    getPreview: async () => {
      try {
        const response = await apiClient.get('/cleanup/preview');
        return response.data;
      } catch (error) {
        logRethrow('Error fetching cleanup preview:', error);
      }
    },

    /**
     * Get detailed cleanup preview
     * @param {Object} params - Preview parameters
     * @returns {Promise} Detailed preview data
     */
    getDetailedPreview: async (params) => {
      try {
        const response = await apiClient.post('/cleanup/preview-detailed', params);
        return response.data;
      } catch (error) {
        logRethrow('Error fetching detailed preview:', error);
      }
    },

    /**
     * Clean a specific collection
     * @param {string} collectionName - Name of collection to clean
     * @param {number} olderThanDays - Delete records older than this many days
     * @param {boolean} confirmed - Whether operation is confirmed
     * @returns {Promise} Cleanup result
     */
    cleanupCollection: async (collectionName, olderThanDays, confirmed = false) => {
      try {
        const response = await apiClient.post(`/cleanup/collection/${collectionName}`, {
          olderThanDays,
          confirmed
        });
        return response.data;
      } catch (error) {
        logRethrow('Error cleaning collection:', error);
      }
    },

    /**
     * Clean audit logs with filters
     * @param {Object} payload - Cleanup filters and options
     * @returns {Promise} Cleanup result
     */
    cleanupAuditLogs: async (payload) => {
      try {
        const response = await apiClient.post('/cleanup/audit-logs', payload);
        return response.data;
      } catch (error) {
        logRethrow('Error cleaning audit logs:', error);
      }
    },

    /**
     * Trigger emergency cleanup
     * @returns {Promise} Emergency cleanup result
     */
    emergencyCleanup: async () => {
      try {
        const response = await apiClient.post('/cleanup/emergency');
        return response.data;
      } catch (error) {
        logRethrow('Error performing emergency cleanup:', error);
      }
    },

    /**
     * Trigger daily cleanup
     * @returns {Promise} Daily cleanup result
     */
    dailyCleanup: async () => {
      try {
        const response = await apiClient.post('/cleanup/daily');
        return response.data;
      } catch (error) {
        logRethrow('Error performing daily cleanup:', error);
      }
    },

    /**
     * Trigger weekly cleanup
     * @returns {Promise} Weekly cleanup result
     */
    weeklyCleanup: async () => {
      try {
        const response = await apiClient.post('/cleanup/weekly');
        return response.data;
      } catch (error) {
        logRethrow('Error performing weekly cleanup:', error);
      }
    },

    /**
     * Trigger monthly cleanup
     * @returns {Promise} Monthly cleanup result
     */
    monthlyCleanup: async () => {
      try {
        const response = await apiClient.post('/cleanup/monthly');
        return response.data;
      } catch (error) {
        logRethrow('Error performing monthly cleanup:', error);
      }
    },

    /**
     * Get cleanup history
     * @param {number} limit - Maximum number of records to return
     * @returns {Promise} Cleanup history
     */
    getHistory: async (limit = 50) => {
      try {
        const response = await apiClient.get(`/cleanup/history?limit=${limit}`);
        return response.data;
      } catch (error) {
        logRethrow('Error fetching cleanup history:', error);
      }
    },

    /**
     * Get failed cleanup operations
     * @param {number} limit - Maximum number of records to return
     * @returns {Promise} Failed cleanups
     */
    getFailed: async (limit = 10) => {
      try {
        const response = await apiClient.get(`/cleanup/failed?limit=${limit}`);
        return response.data;
      } catch (error) {
        logRethrow('Error fetching failed cleanups:', error);
      }
    },

    /**
     * Cancel a cleanup job
     * @param {string} historyId - History entry ID to cancel
     * @param {string} reason - Cancellation reason
     * @returns {Promise} Cancellation result
     */
    cancelCleanup: async (historyId, reason) => {
      try {
        const response = await apiClient.post(`/cleanup/cancel/${historyId}`, { reason });
        return response.data;
      } catch (error) {
        logRethrow('Error cancelling cleanup:', error);
      }
    },

    /**
     * Perform advanced cleanup with soft-delete and export options
     * @param {Object} params - Advanced cleanup parameters
     * @returns {Promise} Advanced cleanup result
     */
    advancedCleanup: async (params) => {
      try {
        const response = await apiClient.post('/cleanup/advanced', params);
        return response.data;
      } catch (error) {
        logRethrow('Error performing advanced cleanup:', error);
      }
    },

    /**
     * Get cleanup status
     * @returns {Promise} Cleanup status
     */
    getStatus: async () => {
      try {
        const response = await apiClient.get('/cleanup/status');
        return response.data;
      } catch (error) {
        logRethrow('Error fetching cleanup status:', error);
      }
    }
  },

  /**
   * Generate PDF from invoice data via backend
   * @param {Object} invoiceData - Form data for PDF generation
   * @param {string} qrCodeBase64 - Optional QR code as base64 data URI
   * @returns {Promise<{data: Blob, headers: Object}>} Response with data and headers
   */
  generatePdf: async (invoiceData, qrCodeBase64 = '') => {
    try {
      const response = await apiClient.post('/invoices/pdf', {
        invoiceData,
        qrCodeBase64
      }, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      logRethrow('Error generating PDF:', error);
    }
  }
};

export { authService, settingsService, irbService, invoiceService, pdfService, auditService, apiService, apiClient };

export default apiService;
